import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Loading from 'common/components/Elements/Loading/Loading';
import SimpleModal from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import downloadFile from 'common/utilities/browser/downloadFile/downloadFile';
import { DEBOUNCE_TIME } from 'common/utilities/timeout/timeout';
import type { CloseEvent } from 'core/components/Elements/DropDown/DropDown';
import { enqueueInfoNotification, enqueueWarningNotification } from 'core/redux/actions/notification/notificationActions';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppThunkDispatch } from 'core/redux/store/configureStore.types';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Handle file download given an URL. Will show a modal for slow download preventing the user from browsing away.
 */
function useFileDownload(
  onFinish: (event?: CloseEvent) => void,
) {
  const dispatch = useDispatch<AppThunkDispatch>();
  const jsfUrl = useSelector((state: ApplicationState) => state.configuration.jsfUrl);
  const [showModal, setShowModal] = useState(false);
  const [startShowModalTimer, setStartShowModalTimer] = React.useState(false);

  React.useEffect(() => {
    let showModalTimer: ReturnType<typeof setTimeout> | undefined;

    if (startShowModalTimer) {
      showModalTimer = setTimeout(() => {
        setShowModal(true);
      }, DEBOUNCE_TIME.showFileDownloadModal);
    } else if (showModalTimer) {
      clearTimeout(showModalTimer);
    }

    return () => {
      if (showModalTimer) clearTimeout(showModalTimer);
    };
  }, [startShowModalTimer]);

  const hideModal = () => {
    setStartShowModalTimer(false);
    setShowModal(false);
  };

  const handleStart = async (url: string, event?: React.MouseEvent<HTMLElement>) => {
    setStartShowModalTimer(true);

    try {
      const fileUrl = url.startsWith('http') ? url : `${jsfUrl}${url}`;
      const filename = await downloadFile(fileUrl);
      hideModal();
      dispatch(enqueueInfoNotification(I18n.utils.hooks.useFileDownload.downloaded(filename)));
    } catch (error) {
      hideModal();
      dispatch(enqueueWarningNotification((error as Error)?.message || (error as Error).toString()));
    } finally {
      onFinish(event);
    }
  };

  function FileDownloadModal() {
    return (
      <SimpleModal
        open
        title={I18n.utils.hooks.useFileDownload.downloading}
        disableClose
        content={(
          <Box mt={2} mb={2} textAlign="center">
            <Loading />
            <Typography>{I18n.utils.hooks.useFileDownload.downloadingInfo}</Typography>
          </Box>
        )}
      />
    );
  }

  function FileDownloadBackdrop() {
    return (
      <Backdrop
        sx={{ backgroundColor: 'transparent', zIndex: 9999 }}
        open
      />
    );
  }

  let FileDownloadLoader: typeof FileDownloadModal | typeof FileDownloadBackdrop | undefined;
  if (showModal) FileDownloadLoader = FileDownloadModal;
  if (!showModal && startShowModalTimer) FileDownloadLoader = FileDownloadBackdrop;

  return {
    FileDownloadLoader,
    onStart: handleStart,
  };
}

export default useFileDownload;
